<template lang="pug">
  v-flex(d-flex)
    v-snackbar(v-model="showAttachmentSizeError" :auto-height="true" color="error")
      div {{ attachmentSizeErrorText }}
    v-card(flat)
      //- Send button
      v-layout(justify-end :class="padding")
        ld-gated-button.primary(
          :feature="LdFeatures.Emails"
          :disabled="isDisabled()"
          @click="handleSendingOptionDialog"
          :usage="toSelected.length"
          :loading="isLoading"
        ) Send
</template>

<script>
import dateFns from 'date-fns';
import { mapActions, mapState, mapGetters } from 'vuex';
import TitleBar from '@/Core/components/TitleBar';
import { timeSlots, timeRules } from '@/Core/utilities';
import { currentDate, formatDate, afterYesterday, convertDate } from '@/Core/filters/dates';
import RecurrencePicker from '@/Communications/components/RecurrencePicker';
import { SEND_OPTIONS } from '@/Communications/config';
import { LdGatedButton, LdFeatures } from 'lion-vue';
import { MAX_ATTACHMENT_SIZE_BYTES, MAX_ATTACHMENT_SIZE_MEGABYTES, RECOMMENDED_ATTACHMENT_SIZE_MEGABYTES } from '../config/email-limits';

// Mocks the recurring data structure
const sendRecurringBaseCase = {
  name: null,
  recurrenceRule: null,
  startDate: null,
};

export default {
  name: 'EmailSendingOptions',
  components: {
    TitleBar,
    RecurrencePicker,
    LdGatedButton,
  },
  props: {
    padding: {
      default: '',
      type: String,
    },
    sendOptionRadioValue: {
      default: 1,
      type: Number,
    },
    recurrenceRuleValid: {
      default: false,
      type: Boolean,
    },
    loading: {
      default: false,
      type: Boolean,
    },
  },
  data() {
    return {
      LdFeatures,
      // Calendar options to select scheduled emails
      initialDateRaw: currentDate(), // The raw date from the calendar component without timezone
      initialDateParsed: new Date(), // The parsed version of initialDateRaw.
      initialTime: dateFns.format(new Date(), 'h:mm a'), // The current time selected, used in combination with date.
      timeRules,
      timeSlots,
      // Calendar options to set recurring intervals
      sendRecurring: sendRecurringBaseCase,
      // Controls the dialog for sending confirmation
      sendingOptionDialog: false,
      afterYesterday,
      sendingScheduleDate: '',
      sendingScheduleTime: '',
      showedAttachmentSizeError: false,
      attachmentSizeErrorText:
        `The attachments on this message total more than the limit of ${MAX_ATTACHMENT_SIZE_MEGABYTES}MB. For best deliverability, we recommend that email attachments total less than ${RECOMMENDED_ATTACHMENT_SIZE_MEGABYTES}MB or that you put links to the files in the body of your email.`,
    };
  },
  computed: {
    ...mapState({
      body: state => state.templates.body,
      subject: state => state.templates.subject,
      toSelected: state => state.recipients.toSelected,
      state: state => state.templates.state,
      isSending: state => state.templates.isSending,
      recurrenceRule: state => state.templates.recurrenceRule,
      currentUser: state => state.userInformation.user,
      selectedEmailAccount: state => state.emailAccounts.selectedEmailAccount,
    }),
    ...mapGetters({
      userHasNylasSend: 'userInformation/userHasNylasSend',
      totalAttachmentSizeBytes : 'templates/totalAttachmentSizeBytes',
      uploadFilesInProgress : 'templates/uploadFilesInProgress',
    }),
    invalidAttachmentSize() {
      return this.totalAttachmentSizeBytes > MAX_ATTACHMENT_SIZE_BYTES;
    },
    showAttachmentSizeError: {
      get() {
        return this.invalidAttachmentSize && !this.showedAttachmentSizeError;
      },
      set() {
        this.showedAttachmentSizeError = true;
      },
    },
    isSendingOptionScheduled() {
      return this.sendOptionRadioValue === SEND_OPTIONS.Scheduled;
    },
    isSendingOptionRecurring() {
      return this.sendOptionRadioValue === SEND_OPTIONS.Recurring;
    },
    isLoading() {
      return this.loading || this.isSending;
    },
  },

  filters: {
    formatDate,
    convertDate,
  },

  watch: {
    /*
     * The DatePicker returns a date string without any time zone.
     * In order for the Javascript Date constructor to parse the date properly,
     * we must transform it into the user's local timezone.
     */
    initialDateRaw(value) {
      if (value) {
        this.initialDateParsed = new Date(`${value} PST`);
      } else {
        this.initialDateRaw = currentDate();
        this.initialDateParsed = new Date();
      }
    },
    /*
     * Watches for any changes in the sending date
     * If anything changes, it should invalidate the current RecurrenceRule
     */
    initialDateParsed(value) {
      if (value) {
        this.sendRecurring = sendRecurringBaseCase;
      }
    },
    /*
     * Watches for any changes in the sending time. If valid, update the vuex store
     */
    initialTime(value) {
      if (value && timeRules[0](value) === true) this.sendingScheduleTime = value;
    },
    /*
     * Callback to set the necessary details on recurrence.
     */
    sendRecurring: {
      handler(value) {
        if (value && value.recurrenceRule && value.startDate) {
          this.setRecurrenceRule(value.recurrenceRule);
          this.sendingScheduleDate = value;
        }
      },
      deep: true,
    },
  },

  mounted() {
    this.sendingScheduleDate = this.initialDateRaw;
    this.sendingScheduleTime = this.initialTime;
  },

  methods: {
    ...mapActions('templates', ['sendEmail', 'setRecurrenceRule']),
    // Conditions where the Send button is disabled
    isDisabled() {
      const emptyBody = !this.body || (this.body && !this.body.trim());
      const emptySubject = !this.subject || (this.subject && !this.subject.trim());
      if (this.userHasNylasSend) {
        return (
          emptyBody ||
          emptySubject ||
          !this.toSelected.length ||
          this.isSending ||
          !this.isScheduledValid() ||
          !this.isSendRecurringValid() ||
          !this.selectedEmailAccount ||
          this.loading ||
          this.uploadFilesInProgress
        );
      }
      return (
        emptyBody ||
        emptySubject ||
        !this.toSelected.length ||
        this.isSending ||
        !this.isScheduledValid() ||
        !this.isSendRecurringValid() ||
        this.loading ||
        this.uploadFilesInProgress
      );
    },
    /*
     * Checks if the scheduled option is selected, ensure the user has inputted something
     */
    isScheduledValid() {
      if (this.isSendingOptionScheduled || this.isSendingOptionRecurring) {
        return (
          !!this.initialDateParsed && !!this.initialTime && timeRules[0](this.initialTime) === true
        );
      }
      return true;
    },
    /*
     * Checks if the recurring option is selected, ensure the user has inputted something
     */
    isSendRecurringValid() {
      if (this.isSendingOptionRecurring) {
        return this.recurrenceRuleValid;
      }
      return true;
    },
    handleSendingOptionDialog() {
      if (this.invalidAttachmentSize) {
        this.showedAttachmentSizeError = false;
        return;
      }
      this.$emit('sending-option-dialog');
    }
  },
};
</script>
